/**
 * App: tenant-gated shell.
 *
 * The entire app renders only after the tenant is resolved:
 *  - loading → branded loading state
 *  - unknown tenant (404) → friendly error state
 *  - backend unreachable → error state, or fallback branding when a cached
 *    config exists from an earlier successful load
 *  - ready → the router inside the branded layout
 *
 * The fallback brand (platform defaults) is used ONLY when the tenant
 * config can't be fetched but a previous good config isn't cached either —
 * in that case the error screen owns the page. We never render pages under
 * a wrong tenant's branding.
 */
import { useEffect } from 'react';
import { useTenant } from '../hooks/useTenant';
import { ErrorState } from '../components/ErrorState';
import { useTranslation } from 'react-i18next';
import { LoadingState } from '../components/LoadingState';
import { useAuthStore } from '../stores/auth';
import { AppRouter } from './router';

export function App(): JSX.Element {
  const { t } = useTranslation();
  const { status, errorMessage, isOfflineWithCache, isUnknownTenant, refetch } = useTenant();
  const restore = useAuthStore((s) => s.restore);

  // Restore the session once on load (silent: guests stay guests).
  useEffect(() => {
    void restore();
  }, [restore]);

  if (status === 'loading' || status === 'idle') {
    return <LoadingState message={t('common.loadingSite')} />;
  }

  if (status === 'error' && !isOfflineWithCache) {
    return (
      <div className="min-h-full bg-stone-50 px-4 py-16">
        <ErrorState
          title={isUnknownTenant ? t('common.siteNotFound') : t('common.somethingWrong')}
          message={errorMessage ?? t('common.siteLoadFailed')}
          detail={t('common.persistentError')}
          onRetry={refetch}
        />
      </div>
    );
  }

  if (isOfflineWithCache) {
    // Degraded mode: render the app on the last-known-good config with a
    // persistent banner. Map tiles come from the CDN and usually still work.
    return (
      <div className="flex min-h-full flex-col">
        <div role="alert" className="bg-amber-100 px-4 py-2 text-center text-sm text-amber-900">
          {t('common.offlineBanner')}{' '}
          <button type="button" className="font-medium underline" onClick={refetch}>
            {t('common.retry')}
          </button>
        </div>
        <div className="flex-1">
          <AppRouter />
        </div>
      </div>
    );
  }

  return <AppRouter />;
}
